import React, { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { CircularProgress } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// BRF Admin-endast routes (dashboard) - endast brfadmin
export const AdminRoute = () => {
  const [isAllowed, setIsAllowed] = useState<undefined | boolean>(undefined);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsAllowed(false);
        return;
      }
      // Hämta användarens Firestore-dokument
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        setIsAllowed(false);
        return;
      }
      const userData = userSnap.data();
      // Tillåt endast brfadmin
      setIsAllowed(userData.role === 'brfadmin');
    });
    return () => unsubscribe();
  }, []);

  if (typeof isAllowed === 'undefined') {
    return (
      <CircularProgress
        color="warning"
        sx={{ display: 'block', margin: 'auto', flex: 1 }}
      />
    );
  }
  return isAllowed ? <Outlet /> : <Navigate replace to="/sign-in" />;
};

// Hyria Admin-endast routes (hyria-admin dashboard)
export const HyriaAdminRoute = () => {
  const [isAllowed, setIsAllowed] = useState<undefined | boolean>(undefined);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsAllowed(false);
        return;
      }
      // Hämta användarens Firestore-dokument
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        setIsAllowed(false);
        return;
      }
      const userData = userSnap.data();
      setIsAllowed(userData.role === 'hyria_admin');
    });
    return () => unsubscribe();
  }, []);

  if (typeof isAllowed === 'undefined') {
    return (
      <CircularProgress
        color="warning"
        sx={{ display: 'block', margin: 'auto', flex: 1 }}
      />
    );
  }
  return isAllowed ? <Outlet /> : <Navigate replace to="/sign-in" />;
};

// Användar-routes (home, create, documents)
export const UserRoute = () => {
  const [isAllowed, setIsAllowed] = useState<undefined | boolean>(undefined);
  const [profileComplete, setProfileComplete] = useState<undefined | boolean>(undefined);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsAllowed(false);
        setProfileComplete(false);
        return;
      }
      // Hämta användarens Firestore-dokument
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        setIsAllowed(false);
        setProfileComplete(false);
        return;
      }
      const userData = userSnap.data();
      // Tillåt vanliga användare (utan role), user, och alla admin-roller
      setIsAllowed(!userData.role || userData.role === 'user' || userData.role === 'brfadmin' || userData.role === 'hyria_admin');

      // Profil-komplett-kontroll enligt feedback
      const requiredFields = ['firstName', 'lastName', 'phoneNumber', 'email'];
      const isComplete = requiredFields.every((key) => Boolean(userData?.[key]));
      setProfileComplete(isComplete);
    });
    return () => unsubscribe();
  }, []);

  if (typeof isAllowed === 'undefined' || typeof profileComplete === 'undefined') {
    return (
      <CircularProgress
        color="warning"
        sx={{ display: 'block', margin: 'auto', flex: 1 }}
      />
    );
  }
  // Om användaren är inloggad men profilen inte är komplett, blockera åtkomst till känsliga vyer
  // Vi låter själva profil-/hem-sidan laddas så användaren kan komplettera
  if (isAllowed && profileComplete === false) {
    // En minimal guard: om route är create/applications/documents, skicka hem
    const currentPath = window.location.pathname;
    const blocked = ['/create', '/applications', '/document'];
    if (blocked.some((p) => currentPath.startsWith(p))) {
      return <Navigate replace to="/" />;
    }
  }
  return isAllowed ? <Outlet /> : <Navigate replace to="/sign-in" />;
};

// Bakåtkompatibilitet - standardexport är UserRoute
const ProtectedRoute = UserRoute;

export default ProtectedRoute;
